import firebase from 'firebase';
import moment from 'moment';
import { FIREBASE_CONFIG } from '../config';

class FireBaseTools {

    constructor() {
        this.firebaseApp = firebase.initializeApp(FIREBASE_CONFIG);
        this.firebaseAuth = this.firebaseApp.auth();
        this.firebaseDb = this.firebaseApp.database();
    }

    /**
     * Return an instance of a firebase auth provider based on the provider string.
     *
     * @param provider
     * @returns {firebase.auth.AuthProvider}
     */
    getProvider = (provider) => {
        switch (provider) {
        case 'email':
                return new firebase.auth.EmailAuthProvider();
        case 'facebook':
                return new firebase.auth.FacebookAuthProvider();
        case 'github':
                return new firebase.auth.GithubAuthProvider();
        case 'google':
                return new firebase.auth.GoogleAuthProvider();
        case 'twitter':
                return new firebase.auth.TwitterAuthProvider();
        default:
        }
    }

    /**
     * Login with provider => p is provider "email", "facebook", "github", "google", or "twitter"
     * Uses Popup therefore provider must be an OAuth provider. EmailAuthProvider will throw an error
     *
     * @returns {any|!firebase.Thenable.<*>|firebase.Thenable<any>}
     */
    loginWithProvider = (p) => {
        var self = this;

        let provider = self.getProvider(p);
        return self.firebaseAuth.signInWithPopup(provider).then(function (result) {
                return self.firebaseAuth.currentUser;
        }).catch(function (error) {
            return {
                errorCode: error.code,
                errorMessage: error.message
            };
        });
    }

    /**
     * Register a user with email and password
     *
     * @param user
     * @returns {any|!firebase.Thenable.<*>|firebase.Thenable<any>}
     */
    registerUser = (user) => {
        var self = this;

        return self.firebaseAuth.createUserWithEmailAndPassword(user.email, user.password).then(user => {
            return user;
        }).catch(error => {
            return {
                errorCode: error.code,
                errorMessage: error.message
            };
        });
    }

    /**
     * Sign the user out
     *
     * @returns {!firebase.Promise.<*>|firebase.Thenable<any>|firebase.Promise<any>|!firebase.Thenable.<*>}
     */
    logoutUser = () => {
        var self = this;

        return self.firebaseAuth.signOut().then(() => {
            return {
                success: 1,
                message: 'logout'
            };
        });
    }

    /**
     * Retrieve the current user (Promise)
     * @returns {Promise}
     */
    fetchUser = () => {
        var self = this;

        return new Promise((resolve, reject) => {
            const unsub = self.firebaseAuth.onAuthStateChanged(user => {
                unsub();
                resolve(user);
            }, error => {
                reject(error);
            })
        })
    }

    /**
     * Log the user in using email and password
     *
     * @param user
     * @returns {any|!firebase.Thenable.<*>|firebase.Thenable<any>}
     */
    loginUser = (user) => {
        var self = this;

        return self.firebaseAuth.signInWithEmailAndPassword(user.email, user.password).then(user => {
            return user;
        }).catch(error => {
            return {
                errorCode: error.code,
                errorMessage: error.message
            }
        });
    }

    /**
     * Update a user's profile data
     *
     * @param u
     * @returns {!firebase.Promise.<*>|firebase.Thenable<any>|firebase.Promise<any>|!firebase.Thenable.<*>}
     */
    updateUserProfile = (u) => {
        var self = this;

        return self.firebaseAuth.currentUser.updateProfile(u).then(() => {
            return self.firebaseAuth.currentUser;
        }, error => {
            return {
                errorCode: error.code,
                errorMessage: error.message
            }
        })
    }

    /**
     * Reset the password given the specified email
     *
     * @param email {string}
     * @returns {!firebase.Promise.<*>|firebase.Thenable<any>|firebase.Promise<any>|!firebase.Thenable.<*>}
     */
    resetPasswordEmail = (email) => {
        var self = this;

        return self.firebaseAuth.sendPasswordResetEmail(email).then(() => {
            return {
                message: 'Email sent'
            }
        }, error => {
            return {
                errorCode: error.code,
                errorMessage: error.message
            }
        });
    }

    /**
     * Update the user's password with the given password
     *
     * @param newPassword {string}
     * @returns {!firebase.Promise.<*>|firebase.Thenable<any>|firebase.Promise<any>|!firebase.Thenable.<*>}
     */
    changePassword = (newPassword) => {
        var self = this;

        return self.firebaseAuth.currentUser.updatePassword(newPassword).then(user => {
            return user;
        }, error => {
            return {
                errorCode: error.code,
                errorMessage: error.message
            }
        });
    }

    /**
     * Send an account email verification message for the currently logged in user
     *
     * @returns {!firebase.Promise.<*>|firebase.Thenable<any>|firebase.Promise<any>|!firebase.Thenable.<*>}
     */
    sendEmailVerification = () => {
        var self = this;

        return self.firebaseAuth.currentUser.sendEmailVerification().then(() => {
            return {
                message: 'Email sent'
            }
        }, error => {
            return {
                errorCode: error.code,
                errorMessage: error.message
            }
        });
    }

    getRef = (ref) => {
        var self = this,
            env = process.env.NODE_ENV;

        if (env === 'development') {
            return self.firebaseDb.ref(`dev/${ref}`);
        }

        return self.firebaseDb.ref(ref);
    }

    checkin = (checkin, allCheckins) => {
        var self = this;

        let id = checkin.id;
        delete checkin.id;

        return self.getRef('checkins/' + id).set(checkin).then(() => {
            return self.getAllCheckins();
        });
    }

    getAllCheckins = () => {
        var self = this;

        return self.getRef('checkins').once('value').then(snapshot => {
            let checkins = [];

            snapshot.forEach((child) => {
                checkins.push({
                    ...child.val(),
                    id: child.key
                })
            });

            return checkins.sort((a, b) => {
                return moment(b.date) - moment(a.date);
            });
        }, (e) => {
            console.error('Error during read: ' + JSON.stringify(e));
        });
    }

    deleteCheckin = (id) => {
        var self = this;

        return self.getRef('checkins/' + id).remove().then(() => {
            return self.getAllCheckins();
        }, (e) => {
            console.error('Error during delete: ' + JSON.stringify(e));
            return false;
        });
    }

    getItinerary = () => {
        var self = this;

        return self.getRef('itinerary').once('value').then(snapshot => {
            let itinerary = {};

            snapshot.forEach((child) => {
                itinerary[child.key] = child.val();
            });

            return itinerary;
        }, (e) => {
            console.error('Error during read: ' + JSON.stringify(e));
        });
    }

    setItinerary = (itinerary) => {
        var self = this;

        return self.getRef('itinerary').set(itinerary).then(() => {
            return self.getItinerary();
        }, (e) => {
            console.error('Error during save: ' + JSON.stringify(e));
        });
    }
}

export default new FireBaseTools();



// WEBPACK FOOTER //
// ./src/app/utils/firebase.js